import { useMemo } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../elevio.actions';
import { elevioSelectors } from '../elevio.reducer/selectors';
import { ElevioError, getUrl, noOp } from '../utils';
import { transformMessagesToConversationThread } from '../utils/messages';
import { transformResolutionSearchArticles } from '../utils/suggestions';

import { useKcsSettings } from './useKcsSettings';

import { useSearchArticlesInResolution } from './index';

type UseResolutionModalFooterProps = {
  confirmLabel: string;
  confirmOnClickHandler: () => void;
  cancelLabel: string;
  cancelOnClickHandler: () => void;
  disabled: boolean;
};
/**
 * Function to return the props for the resolution modal footer, depending on
 * the stage of the resolution
 */
export function useResolutionModalFooterProps(): UseResolutionModalFooterProps {
  const { kcsSettingsData } = useKcsSettings();

  let showCloseButton = true;
  if (
    kcsSettingsData &&
    kcsSettingsData.kcsSettings &&
    kcsSettingsData.kcsSettings.showCloseButton === false
  ) {
    showCloseButton = false;
  }

  const articleSearchTerm = useAppSelector(
    elevioSelectors.resolution.articleSearchTerm,
  );
  const articleSolved = useAppSelector(
    elevioSelectors.resolution.articleSolved,
  );
  const draft = useAppSelector(elevioSelectors.resolution.draft);
  const selectedArticleTitle = useAppSelector(
    elevioSelectors.resolution.selectedArticleTitle,
  );
  const selectedArticleId = useAppSelector(
    elevioSelectors.resolution.selectedArticleId,
  );
  const stage = useAppSelector(elevioSelectors.resolution.stage);
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const conversationSubject = useAppSelector(
    elevioSelectors.conversation.subject,
  );
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  const dispatch = useAppDispatch();

  const { data } = useSearchArticlesInResolution({
    searchTerm: articleSearchTerm,
  });
  const suggestions = useMemo(
    () => transformResolutionSearchArticles(data),
    [data],
  );
  const suggestedArticleIds = suggestions.map((s) => s.id);

  if (!conversationId) {
    throw ElevioError({
      context: 'useResolutionModalFooterProps',
      feature: 'resolution',
      message: 'no conversationId found',
    });
  }

  const messages = useAppSelector(elevioSelectors.conversation.messages);
  const tagNames = useAppSelector(elevioSelectors.conversation.tagNames);
  const conversationThread = transformMessagesToConversationThread(messages);

  const unsetCancelWarning = () =>
    dispatch(elevioActions.resolution.unsetCancelWarning());
  const setCancelWarning = () =>
    dispatch(elevioActions.resolution.setCancelWarning());

  const cancelOnClickHandler = !showCloseButton
    ? noOp // Cannot provide 'undefined' as callback, must provide some callback. noOp callback is used if !showCloseButton
    : stage === 'cancelWarning'
    ? unsetCancelWarning
    : setCancelWarning;

  const submitLabel = `Submit`;
  const cancelLabel = 'Cancel';

  const confirmSetNoSolutionSubmitted = () => {
    dispatch(
      elevioActions.resolution.setNoSolutionSubmitted({
        suggestedArticleIds,
        ticketId: conversationId.toString(),
        ticketTags: tagNames,
        ticketSubject: conversationSubject,
        ticketThread: conversationThread,
        url: getUrl(),
      }),
    );
  };

  const confirmSetNoSolutionDraftArticleSubmitted = () => {
    if (draft) {
      dispatch(
        elevioActions.resolution.setNoSolutionDraftArticleSubmitted({
          ticketId: conversationId.toString(),
          ticketThread: conversationThread,
          ticketTitle: draft.question,
          suggestedArticleIds,
          ticketSubject: conversationSubject,
          ticketTags: tagNames,
          url: getUrl(),
          notes: draft.notes,
        }),
      );
    }
  };

  if (articleSolved && selectedArticleId) {
    return {
      confirmLabel: submitLabel,
      confirmOnClickHandler: () => {
        if (!selectedArticleId || !selectedArticleTitle) {
          throw ElevioError({
            context: 'useResolutionModalFooterProps',
            feature: 'resolution',
            message: !selectedArticleId
              ? 'no selectedArticleId found'
              : 'no selectedArticleTitle found',
          });
        }

        dispatch(
          elevioActions.resolution.setSolutionSubmitted({
            helpfulArticleId: selectedArticleId,
            suggestedArticleIds,
            ticketId: conversationId.toString(),
            ticketSubject: conversationSubject,
            ticketTags: tagNames,
            ticketThread: conversationThread,
            url: window.location.href,
            articleTitle: selectedArticleTitle,
            languageId,
          }),
        );
      },
      cancelLabel,
      cancelOnClickHandler,
      disabled: false,
    };
  }

  if (!draft && stage === 'noSolutionNoDraftArticle') {
    return {
      confirmLabel: submitLabel,
      confirmOnClickHandler: confirmSetNoSolutionSubmitted,
      cancelLabel,
      cancelOnClickHandler,
      disabled: false,
    };
  }

  if (draft && stage === 'noSolutionDraftArticle') {
    return {
      confirmLabel: submitLabel,
      confirmOnClickHandler: confirmSetNoSolutionDraftArticleSubmitted,
      cancelLabel,
      cancelOnClickHandler,
      disabled: false,
    };
  }

  if (stage === 'cancelWarning') {
    return {
      confirmLabel: 'Yes, resolve without solution',
      confirmOnClickHandler: confirmSetNoSolutionSubmitted,
      cancelLabel: 'No, go back',
      cancelOnClickHandler,
      disabled: false,
    };
  }

  return {
    confirmLabel: submitLabel,
    confirmOnClickHandler: noOp, // no handler as this will be only used in a disabled state
    cancelLabel,
    cancelOnClickHandler,
    disabled: true,
  };
}
